<template>
  <panel-layout
    id="panel"
    :loading="loading"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
  >
    <template #actions-right>
      <div v-if="customerPass" class="flex items-center space-x-1">
        <o-tooltip class="h-8" :text="hasSale ? $t('order.view') : $t('order.new')">
          <o-button
            v-if="!hasSale"
            flat
            size="slim"
            icon="sale"
            @click="handleCreateOrder()"
          />

          <o-panel-link
            v-if="hasSale"
            tag="o-button"
            :path="`/order/${customerPass.order.idOrder}`"
            flat
            size="slim"
            icon="sale"
          />
        </o-tooltip>

        <o-tooltip v-if="customerPass.isGift && customerPass.claimable" class="h-8" :text="$t('components.customerPass.actions.claim.title')">
          <o-button
            size="slim"
            flat
            icon="link"
            variant="primary"
            @click="handleClaim"
          />
        </o-tooltip>

        <o-tooltip class="h-8" :text="$t('pass.emailCustomer')">
          <o-button
            size="slim"
            flat
            icon="email"
            @click="handleEmail"
          />
        </o-tooltip>

        <o-history-link :item="customerPass" model="CustomerPass" icon="history" slim flat />

        <o-tooltip v-if="!customerPass.deleted" class="h-8" :text="$t('pass.deleteCustomer')">
          <o-button
            size="slim"
            flat
            icon="delete"
            @click="handleDelete"
          />
        </o-tooltip>

        <o-tooltip v-if="customerPass.deleted" class="h-8" :text="$t('pass.restoreCustomer')">
          <o-button
            size="slim"
            flat
            icon="undo"
            @click="handleRestore"
          />
        </o-tooltip>
      </div>
    </template>

    <template #heading>
      <div v-if="customerPass" class="flex items-center space-x-2">
        <div>
          <h2 class="text-lg md:text-2xl tracking-tighter font-bold text-gray-800/85 dark:text-gray-100 leading-none">
            {{ $t('general.colonSeparator', { label1: $t('pass.edit'), label2: customerPass.pass ? customerPass.pass.name : '' }) }}
          </h2>
          <div class="mt-2 mb-4 text-gray-700 dark:text-gray-200 text-sm">
            <div class="flex items-center space-x-2">
              <o-chip flat size="xs" square>
                {{ $t('pass.usesRemaining') }}: {{ customerPass.isUnlimitedUse ? $t('pass.unlimited') : customerPass.usesRemaining }}
              </o-chip>
              <o-chip flat size="xs" square>
                {{ customerPass.expires ? $t('pass.expiryDate') + ': ' + $date.format(customerPass.expiryDate * 1000, $date.presets.long) : $t('pass.neverExpires') }}
              </o-chip>
            </div>
            <!--<p class="leading-none">
              <span class="font-medium mr-1">Created: </span>{{ $date.format(customerPass.saturated, $date.presets.full) }}
            </p>-->
          </div>
          <div class="form-item--status mr-4">
            <o-chip v-if="isExpired" flat variant="warning" size="xs" square>
              {{ $t('general.expired') }}
            </o-chip>
            <o-chip v-if="isExpended" flat variant="warning" size="xs" square>
              {{ $t('pass.expended') }}
            </o-chip>
            <o-chip v-if="isPaid" flat variant="success" size="xs" square>
              {{ $t('general.paid') }}
            </o-chip>
            <o-chip v-if="isDeleted" flat variant="danger" size="xs" square>
              {{ $t('general.deleted') }}
            </o-chip>
          </div>
        </div>
      </div>
    </template>

    <div v-if="customerPass" class="h-full">
      <o-scroll class="px-5">
        <dl class="py-4 md:py-6">
          <o-description-details
            :term="$t('general.customer')"
            class="pb-0"
            center
          >
            <template v-if="customerPass.gift && customerPass.claimable">
              <template v-if="customerPass.gift.to.name">
                {{ customerPass.gift.to.name }}
              </template>
              <template v-if="customerPass.gift.to.email">
                ({{ customerPass.gift.to.email }})
              </template>
            </template>
            <customer-select v-else v-model="customerPass.customer" preview create default-status-class="bg-white dark:bg-gray-900 h-10" />
          </o-description-details>

          <o-description-details
            v-if="!isUnlimitedUse || isDev || isAdmin"
            :term="$t('pass.usesRemaining')"
            class="pb-0"
            center
          >
            <number-field v-model="customerPass.usesRemaining" />
            <p v-if="isUnlimitedUse" class="text-sm text-gray-500 dark:text-gray-400">
              This pass is current set to unlimited uses. Editing is only available for admin users.
            </p>
          </o-description-details>

          <o-description-details
            v-if="customerPass.gift && !customerPass.isGiftDelivered"
            :term="$t('general.deliveryDate')"
            class="pb-0"
            center
            date
          >
            <date-field
              v-model="customerPass.gift.deliveryDate"
              :augmenter="Augmenters.timestamp"
              :min-date="new Date()"
            />
          </o-description-details>

          <o-description-details
            v-if="!neverExpires || isDev || isAdmin"
            :term="$t('general.expiryDate')"
            class="pb-0"
            center
            date
          >
            <date-field v-model="customerPass.expiryDate" :augmenter="Augmenters.timestamp" />
            <p v-if="neverExpires" class="text-sm text-gray-500 dark:text-gray-400">
              This pass is current set to never expire. Editing is only available for admin users.
            </p>
          </o-description-details>
        </dl>

        <div class="mt-10">
          <h4 class="mb-3 font-medium text-gray-700 dark:text-gray-200">
            {{ $t('general.reservations') }}
          </h4>
          <o-table
            class="border rounded"
            :data="customerPass.allReservations"
            :headers="[
              {text: $t('general.services'), sortKey: 'servicesLabel'},
              {text: $t('general.customer'), sortKey: 'customer.displayName'},
              {text: $t('general.date')},
              {text: $t('general.uses')},
              {text: $t('general.actions')}
            ]"
            :limit="20"
            :tbody-class="{
              tfoot: '',
              tr: 'transition-all duration-200 bg-gray-50 dark:bg-gray-900',
              td: 'py-3 px-4 text-gray-800 dark:text-gray-100 text-sm',
              stripe: 'bg-gray-50/50 dark:bg-gray-900/20'
            }"
            :thead-class="{
              thead: 'border-b',
              tr: 'border-blue-700',
              th: 'font-normal p-4 pb-3 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-200 text-sm sticky top-0',
              content: 'flex item-center'
            }"
            striped
            :placeholder="$t('pages.customer.pass.id.noReservations')"
          >
            <template #row="{ row, tdClass }">
              <td :class="tdClass">
                {{ row._servicesLabel }}
              </td>
              <td :class="tdClass">
                {{ row.customer ? row.customer.displayName : '-' }}
              </td>
              <td :class="tdClass">
                {{ row.dateLabel }}
              </td>
              <td :class="tdClass">
                {{ row.customerPassUses || '0' }}
              </td>
              <td :class="tdClass">
                <o-tooltip :text="$t('pages.customer.pass.id.viewReservation')">
                  <o-panel-link
                    tag="o-button"
                    flat
                    outline
                    :path="`/appointment/${row.idReservation}?date=${row.date}`"
                    icon="view"
                    size="sm"
                  />
                </o-tooltip>
              </td>
            </template>
          </o-table>
        </div>
      </o-scroll>
    </div>

    <template #footer>
      <div v-if="customerPass" class="flex justify-between">
        <div>
          <o-button flat outline @click="cancel()">
            {{ $t('general.cancel') }}
          </o-button>
        </div>

        <div>
          <o-button variant="primary" icon="save" outline @click="save()">
            {{ $t('general.save') }}
          </o-button>

          <o-button variant="primary" icon="save" @click="save(true)">
            {{ $t('general.saveAndClose') }}
          </o-button>
        </div>
      </div>
    </template>

    <delete-dialog
      v-if="customerPass"
      ref="deleteDialog"
      :customerPass="customerPass"
      :portal="panel.portal"
      v-model:loading="loading"
      @success="cancel(true)"
    />

    <restore-dialog
      v-if="customerPass"
      ref="restoreDialog"
      v-model:customerPass="customerPass"
      :portal="panel.portal"
      v-model:loading="loading"
    />

    <email-dialog
      v-if="customerPass"
      ref="emailDialog"
      :portal="panel.portal"
      v-model:loading="loading"
    />

    <claim-dialog
      v-if="customerPass"
      ref="claimDialog"
      :portal="panel.portal"
      v-model:loading="loading"
      @success="handleClaimSuccess"
    />

    <create-order-dialog
      v-if="customerPass"
      ref="createOrderDialog"
      :portal="panel.portal"
      v-model:loading="loading"
      @success="handleCreateOrderSuccess"
    />

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
// @ts-ignore
import CustomerSelect from '@/components/customer/elements/Select.vue'
// @ts-ignore
import CreateOrderDialog from '@/components/order/actions/CreateOrder.vue'
// @ts-ignore
import DeleteDialog from '@/components/customerPass/actions/Delete.vue'
// @ts-ignore
import RestoreDialog from '@/components/customerPass/actions/Restore.vue'
// @ts-ignore
import EmailDialog from '@/components/customerPass/actions/Email.vue'
// @ts-ignore
import ClaimDialog from '@/components/customerPass/actions/Claim.vue'

// @ts-ignore
import {
  OTable,
  OFieldGroup,
  ODescriptionDetails,
} from '@/components/elements'

// @ts-ignore
import {
  NumberField,
  TextAreaField,
  DateField,
} from '@/components/fields'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

import { usePanel } from '~/composables/usePanel'

const props = defineProps<{ id?: string }>()

const { $api, $bus, $notification, $i18n, $date } = useNuxtApp()
const runtimeConfig = useRuntimeConfig()
const { info } = useAuth()

const panel = usePanel()

const deleteDialog = ref<any>()
const restoreDialog = ref<any>()
const emailDialog = ref<any>()
const claimDialog = ref<any>()
const createOrderDialog = ref<any>()
const confirmDialog = ref<any>()

const customerPass = ref<any>(null)
const neverExpires = ref<any>(null)
const loading = ref(true)

const isDev = computed(() => runtimeConfig.public?.instance === 'dev' || runtimeConfig.instance === 'dev')
const isAdmin = computed(() => !!info.value?.adminUser)
const isUnlimitedUse = computed(() => customerPass.value.isUnlimitedUse)
const isValid = computed(() => customerPass.value.isValid)
const isExpired = computed(() => customerPass.value.isExpired)
const isExpended = computed(() => customerPass.value.isExpended)
const isPaid = computed(() => customerPass.value.isPaid)
const isDeleted = computed(() => customerPass.value.deleted)
const hasSale = computed(() => !!customerPass.value.order)

const { unsavedChanges, snapshot } = useUnsavedChanges(customerPass)

watch(customerPass, () => {
  neverExpires.value = customerPass.value.neverExpires
})

const handleDelete = () => {
  deleteDialog.value.open(customerPass.value)
}

const handleRestore = () => {
  restoreDialog.value.open(customerPass.value)
}

const handleEmail = () => {
  emailDialog.value.open(customerPass.value)
}

const handleClaim = () => {
  claimDialog.value.open(customerPass.value)
}

const handleClaimSuccess = (claimedPass: any) => {
  customerPass.value = claimedPass
  snapshot()
}

const fetchCustomerPass = async (idCustomerPass?: string) => {
  loading.value = true

  try {
    if (idCustomerPass) {
      customerPass.value = (await $api.customerPass.get(idCustomerPass)).data
      snapshot()
    }
  } catch (e) {
    console.log(e)
  }

  loading.value = false
}

const save = async (close = false) => {
  loading.value = true

  try {
    const result = (await $api.customerPass.save(customerPass.value)).data

    if (result) {
      customerPass.value = result
      snapshot()

      $notification.toast({
        title: $i18n.t('pages.customer.pass.id.success'),
        text: $i18n.t('pages.customer.pass.id.successInfo'),
        variant: 'success',
      })

      $bus.emit.updated('CustomerPass', result)

      if (close) {
        panel.close()
      }
    }
  } catch (e: any) {
    return false
  }

  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const handleCreateOrder = () => {
  const customer = customerPass.value.customer
  const date = new Date()
  const items = [customerPass.value]

  const data = {
    customer,
    date,
    items,
  }

  createOrderDialog.value.open(data)
}

const handleCreateOrderSuccess = () => {
  fetchCustomerPass(props.id)
}

watch(() => props.id, (id) => fetchCustomerPass(id), { immediate: true })
</script>
